
import { ComputedRef, defineComponent, onMounted, watch } from "vue";
import { computed } from "@vue/reactivity";
import {
  Alternative,
  ExerciseStudent,
  Question,
  QuestionType
} from "./helpers";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { dispatchError } from "../../utils/error_handling";
import TUIViewer from "../../components/application/TUIViewer.vue";
import QuestionnaireFooter from "./QuestionnaireFooter.vue";
import {
  finishQuestionnaire,
  questionErrorHandler,
  redirectToQuestionnaireCover,
  reloadContent,
  reloadContentList,
  showMaximumTimeReachedModal
} from "./questionnaire_error_handler";
import { shuffle } from "../../components/mixins";
import { scrollParentToElement } from "../../utils/scroll";
import { wrapLoading } from "../../utils/wrap_loading";
import TimeFormatter from "./TimeFormatter.vue";
import QuestionAlternative from "./QuestionAlternatives.vue";
import QuestionAlternatives from "./QuestionAlternatives.vue";
import QuestionDescriptive from "./QuestionDescriptive.vue";
import Image from "./Image.vue";

export default defineComponent({
  name: "Questionnaire",
  components: {
    Image,
    QuestionDescriptive,
    QuestionAlternatives,
    QuestionAlternative,
    QuestionnaireFooter,
    TUIViewer,
    TimeFormatter
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const {
      numberOfQuestions,
      isAbleToRedoQuestions
    }: ExerciseStudent = store.getters["content_module/exercise/exercise"];

    const question: ComputedRef<Question> = computed(
      () => store.getters["content_module/questionnaire/currentQuestion"]
    );

    const alternatives: ComputedRef<Alternative[]> = computed(
      () => store.getters["content_module/questionnaire/alternatives"]
    );

    onMounted(async () => {
      try {
        await wrapLoading(async () => {
          await loadQuestion();
        }, "Carregando exercício");
      } catch (e) {
        console.error("[onMounted] Error ", e);
        await questionErrorHandler(e);
      }
    });

    const loadQuestion = async (): Promise<void> => {
      try {
        const currentRoute = router.currentRoute.value;
        if (!currentRoute) return;
        await store.dispatch(
          "content_module/questionnaire/setCurrentQuestion",
          currentRoute.params.page
        );
      } catch (e) {
        console.error("[setCurrentQuestion] Error ", e);
        throw e;
      }
    };

    watch(question, () => onCurrentQuestionUpdate());

    const onCurrentQuestionUpdate = async (): Promise<void> => {
      try {
        await reloadQuestionControls();
        await updateQuestionPageOnURL();
        await shuffleAlternatives();
      } catch (e) {
        console.error("[onCurrentQuestionUpdate] Error ", e);
        await questionErrorHandler(e);
      }
    };

    const reloadQuestionControls = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/questionnaire/questionnaire_control/reload"
        );
      } catch (e) {
        console.error("[reloadQuestionControls] Error ", e);
        throw e;
      }
    };

    const updateQuestionPageOnURL = async (): Promise<void> => {
      try {
        const currentRoute = router.currentRoute.value;
        if (!currentRoute) return;
        await router.push({
          name: currentRoute.name as string,
          params: { ...currentRoute.params, page: question.value.page }
        });
      } catch (e) {
        console.error("[reloadQuestionControls] Error ", e);
        throw e;
      }
    };

    const shuffleAlternatives = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/questionnaire/shuffleAlternatives"
        );
      } catch (e) {
        console.error("[shuffleAlternatives] Error ", e);
        throw e;
      }
    };

    const onSelectAlternative = async (
      alternative: Alternative
    ): Promise<void> => {
      try {
        if (!isAbleToRedoQuestions && question.value.status.attempts > 0)
          return;
        if (
          question.value.attemptLimit !== null &&
          question.value.status.attempts === question.value.attemptLimit
        ) {
          return;
        }
        await replyQuestion(alternative);
        scrollToFeedbackBanner();
        await shuffleAlternatives();
        /** TODO: SHOULD NOT USE THIS. */
        await loadAllContents();
      } catch (e) {
        console.error("[onSelectAlternative] Error ", e);
        await questionErrorHandler(e);
      }
    };

    const replyQuestion = async (alternative: Alternative): Promise<void> => {
      try {
        const payload = {
          page: question.value.page,
          alternativeID: alternative.id
        };
        await store.dispatch(
          "content_module/questionnaire/replyQuestionByPage",
          payload
        );
      } catch (e) {
        console.error("[replyQuestion] Error ", e);
        throw e;
      }
    };

    const loadAllContents = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/loadAllContents");
      } catch (e) {
        console.error("[loadContents] Error dispatch ", e);
        await dispatchError(e);
      }
    };

    const scrollToFeedbackBanner = () => {
      const parentElement: HTMLElement | null = document.querySelector(
        "[data-questionnaire-view]"
      );
      const element: HTMLElement | null = document.querySelector(
        "[data-feedback]"
      );
      if (!parentElement || !element) {
        return;
      }
      scrollParentToElement(parentElement, element, 500);
    };

    const onClickExit = async () => {
      try {
        await redirectToQuestionnaireCover();
      } catch (e) {
        console.error("[onClickExit] Error ", e);
        await dispatchError(e);
      }
    };

    const onTimeExpired = async (): Promise<void> => {
      try {
        await showMaximumTimeReachedModal();
        await finishQuestionnaire();
        await reloadContent();
        await reloadContentList();
        await redirectToQuestionnaireCover();
      } catch (e) {
        console.error("[onTimeExpired] Error ", e);
        await dispatchError(e);
      }
    };

    return {
      question,
      alternatives,
      numberOfQuestions,
      isAbleToRedoQuestions,
      QuestionType,
      onSelectAlternative,
      shuffle,
      onClickExit,
      onTimeExpired
    };
  }
});
